import { compareDates } from "./utils";
import type { IIntermediateSplitConfig } from "./intermediateSplitConfig";
import { getDeviceId } from "../utils/getDeviceId";
import type { IDriverChange } from "../interfaces/driverChange";
import type { IDutyStatusLog, TDutyStatusLogStatus } from "../interfaces/dutyStatusLog";
import type { IViolation } from "../interfaces/violation";
import type { TDiscreteAvailabilitySummary } from "./availabilityDiscrete";
import type { IEmergencyResponsePeriod } from "./exemptions/emergencyResponse";
import type { IIdDictionary, IIdEntity } from "../interfaces/entity";
import type { IEwdWorkRestAvailability, IHosRuleset, IHosRulesetConfig } from "../interfaces/hosRuleset";
import type { IUserHosRuleSet } from "../interfaces/userHosRuleSet";
import type { TDeferralStatus, THosMalfunction, THosStatus, TViolationType } from "../interfaces/statues";
import { getEffectiveRuleSets, RULESETS, MAX_REPORT_CYCLE_DAYS } from "./rulesets";
import DutyStatusIntervalTracker from "../utils/dutyStatusIntervalTracker";

export interface IDeviceAssociation {
    deviceId: string;
    fromDate: Date;
    toDate: Date;
}

export interface ILatestMalfunctionDiagnosticDictionary<T = IDutyStatusLog> {
    malfunction?: IIdDictionary<T>;
    diagnostic?: IIdDictionary<T>;
}

export interface IPeriod {
    fromDate: Date;
    toDate: Date;
}

export type TSplitPeriodType = "offDuty" | "sleeperBerth";

export interface IOffDutySplitPeriod extends IPeriod {
    type: TSplitPeriodType & "offDuty";
}

export interface ISbSplitPeriod extends IPeriod {
    type: TSplitPeriodType & "sleeperBerth";
}

export interface IOffDutyMinimumWorkdayReset {
    type: "offDutyMinimum";
    fromDate: Date;
    toDate: Date;
    eligibleForResetDate: Date;
}

export interface IAsymmetricSplitReset {
    type: "asymmetricSplitSleeperBerth";
    periods: [IOffDutySplitPeriod | ISbSplitPeriod] | [IOffDutySplitPeriod, ISbSplitPeriod] | [ISbSplitPeriod, IOffDutySplitPeriod];
    eligibleForResetDate?: Date;
}

export interface ISymmetricSplitReset {
    type: "symmetricSplitSleeperBerth";
    periods: [ISbSplitPeriod] | [ISbSplitPeriod, ISbSplitPeriod];
    eligibleForResetDate?: Date;
}

export type TWorkdayResetInfo = IOffDutyMinimumWorkdayReset | IAsymmetricSplitReset | ISymmetricSplitReset;

const reISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;

interface IViolationPhrasesDict {
    shortString: string;
    longString: string;
}

export enum DeferralStatus {
    DayOne = "1",
    DayTwo = "2",
    None = "0"
}

export interface ISlimDutyStatusLog<TDate extends string | Date = string> {
    dateTime: TDate;
    sequence?: string;
}

export const offDutyStatuses = ["OFF", "SB", "PC", "PC_Exempted", "WT"];
export const onDutyStatuses = ["ON", "D", "YM"];
export const drivingWhileInExemptionStatuses: THosStatus[] = ["DrivingWhileInExemption", "DrivingStoppedWhileInExemption"];
export const exemptStartStatuses: THosStatus[] = ["YM", "PC"];
export const SECONDS = 1000,
    MINUTES = 60 * SECONDS,
    HOURS = 60 * MINUTES,
    DAYS = 24 * HOURS,
    STANDARD_STATUS_LOG_TYPES: THosStatus[] = ["ON", "OFF", "D", "SB"],
    ADDITIONAL_STATUS_LOG_TYPES: THosStatus[] = ["YM", "PC", "PC_Exempted", "WT"],
    STATUS_LOG_TYPES: THosStatus[] = STANDARD_STATUS_LOG_TYPES.concat(ADDITIONAL_STATUS_LOG_TYPES),
    AUTHORITY_LOG_TYPES: THosStatus[] = ["Authority"],
    EXEMPTION_LOG_TYPES: THosStatus[] = ["Exemption16H", "AdverseWeather", "ExemptionOffDutyDeferral", "RailroadExemption", "ExemptionReducedRest", "EmergencyResponseExemptionEnabled", "EmergencyResponseExemptionDisabled"],
    SYSTEM_STATUSES: THosStatus[] = [
        "MotionStopped",
        "HosEnabled",
        "HosDisabled",
        "MaxPCLimitReached",
        "Authority",
        "RailroadExemption",
        "WorkExemption",
        "AdverseWeather",
        "ExemptionReducedRest",
        "Exemption16H",
        "DrivingWhileInExemption",
        "DrivingStoppedWhileInExemption",
        "EmergencyResponseExemptionEnabled",
        "EmergencyResponseExemptionDisabled"
    ],
    INTERMEDIATE_LOG_TYPES: THosStatus[] = ["INT_D", "INT_PC", "INT_CoDriver"],
    CONTROL_LOG_TYPES: THosStatus[] = EXEMPTION_LOG_TYPES.concat(["Connected", "Disconnected"]),
    OTHER_LOG_TYPES: THosStatus[] = [
        "Login",
        "Logoff",
        "Certify",
        "EnginePowerup",
        "EngineShutdown",
        "EnginePowerupPC",
        "EngineShutdownPC",
        "PowerCompliance",
        "EngineSyncCompliance",
        "TimingCompliance",
        "PositioningCompliance",
        "DataRecordingCompliance",
        "DataTransferCompliance",
        "OtherCompliance",
        "MissingElementCompliance",
        "UnidentifiedDrivingCompliance",
        "SituationalDrivingClear",
        "MaxPCLimitReached",
        "DrivingWhileInExemption",
        "DrivingStoppedWhileInExemption"
    ],
    CANADIAN_COMPLIANCE_LOG_TYPES: THosStatus[] = [
        "CanadaCycleOne",
        "CanadaCycleTwo",
        "OperatingZoneCanadaSouthOf60",
        "OperatingZoneCanadaNorthOf60",
        "OperatingZoneAmerica"
    ],
    AUSTRALIAN_LOG_TYPES: THosStatus[] = ["Work", "Rest"],
    ADDITIONAL_AUSTRALIAN_LOG_TYPES: THosStatus[] = ["WorkExemption"],
    ALL_AUSTRALIAN_LOG_TYPES = AUSTRALIAN_LOG_TYPES.concat(ADDITIONAL_AUSTRALIAN_LOG_TYPES),
    ALL_STATUS_LOG_TYPES: THosStatus[] = STATUS_LOG_TYPES.concat(ALL_AUSTRALIAN_LOG_TYPES),
    DEFAULT_RETENTION_PERIOD = 14,
    AUSTRALIARULESETS = ["AustraliaStandardHoursSoloExemptionHours"],
    CANADARULESETS: string[] = [
        "HosRuleSetCanadaCycleOne",
        "HosRuleSetCanadaCycleTwo",
        "HosRuleSetCanadaCycleOneTeam",
        "HosRuleSetCanadaCycleTwoTeam",
        "HosRuleSetCanadaNorthOf60CycleOne",
        "HosRuleSetCanadaNorthOf60CycleTwo",
        "HosRuleSetCanadaNorthOf60CycleOneTeam",
        "HosRuleSetCanadaNorthOf60CycleTwoTeam",
        "HosRuleSetCanadaOil",
        "HosRuleSetCanadaNorthOf60Oil",
        "HosRuleSetCanadaOilTeam",
        "HosRuleSetCanadaNorthOf60OilTeam",
        "HosRuleSetCanadaBCLoggingTruck",
        "HosRuleSetAlberta"
    ],
    // Update UserHosRuleSetBridge if updating this list
    CYCLEONERULESETS: string[] = [
        "CanadaCycleOne",
        "CanadaCycleOneTeam",
        "CanadaNorthOf60CycleOne",
        "CanadaNorthOf60CycleOneTeam",
        "CanadaBCLoggingTruck"
    ],
    CYCLETWORULESETS: string[] = [
        "CanadaCycleTwo",
        "CanadaCycleTwoTeam",
        "CanadaNorthOf60CycleTwo",
        "CanadaNorthOf60CycleTwoTeam",
        "CanadaOil",
        "CanadaOilTeam",
        "CanadaNorthOf60Oil",
        "CanadaNorthOf60OilTeam"
    ],
    UNIDENTIFIED_COMPLIANCE_LOG_TYPES: string[] = ["D", "ON", "INT_D", "EnginePowerup", "EngineShutdown"],
    ENGINE_LOG_TYPES: string[] = ["EnginePowerup", "EngineShutdown", "EnginePowerupPC", "EngineShutdownPC"],
    MALFUNCTION_LOG_TYPES: string[] = [
        "PowerCompliance",
        "EngineSyncCompliance",
        "TimingCompliance",
        "PositioningCompliance",
        "DataRecordingCompliance",
        "DataTransferCompliance",
        "OtherCompliance",
        "MissingElementCompliance",
        "UnidentifiedDrivingCompliance"
    ],
    CANADA_MAXPC_LIMIT = 75.0,
    ALL_HOS_STATUS: THosStatus[] = STATUS_LOG_TYPES.concat(AUTHORITY_LOG_TYPES)
        .concat(INTERMEDIATE_LOG_TYPES)
        .concat(CONTROL_LOG_TYPES)
        .concat(OTHER_LOG_TYPES),
    DEFAULT_HOS_RULESETS = ["HosRuleSetNone", "HosRuleSetNone8Day", "HosRuleSetNoneCanada", "HosRuleSetNoneCanadaNorthOf60"];

export function getTranslatedRulesetName(ruleset: string | IHosRuleset, translate: (s: string) => string, useLegacyTexasShortHaulRulesets = false) {
    if (typeof ruleset === "string") {
        let sanitizedId = ruleset.replace("HosRuleSet", "");
        let rulesetObj = getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[sanitizedId];
        let stringToTranslate = rulesetObj ? rulesetObj.name : sanitizedId;

        return translate(stringToTranslate);
    }

    return translate(ruleset.name);
}

/**
 * Standard date index used in rule calculators
 */
export function getDateIndex(utcDate: Date, toLocalTime: (date: Date) => Date) {
    let date = toLocalTime(utcDate);
    return date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
}

/**
 * Get the number of "days" between two dates
 */
export function getNumberOfDaysBetween(utcDate1: Date, utcDate2: Date, toLocalTime: (date: Date) => Date) {
    let d1 = toLocalTime(utcDate1);
    let d2 = toLocalTime(utcDate2);
    let startOfDayTime = (d: Date): number => {
        return d.getTime() - d.getHours() * 3600e3 - d.getMinutes() * 60e3 - d.getSeconds() * 1000 - d.getMilliseconds();
    };
    return Math.round(Math.abs((startOfDayTime(d1) - startOfDayTime(d2)) / DAYS));
}

export function getBeginningOfDay(utcDate: Date, toLocalTime: (date: Date) => Date, toUtcTime: (date: Date) => Date, numDaysOffset = 0): Date {
    let beginningOfDay = getBeginningOfDayImpl(utcDate, toLocalTime, toUtcTime, numDaysOffset);
    let beginningOfNextDay = getBeginningOfDayImpl(beginningOfDay, toLocalTime, toUtcTime, 1);

    /* On DST end, some range of local datetimes will occur twice. If the browser's local timezone observes DST
    and getBeginningOfDay() is called with param utcDate inside the range of dates that occur twice (when converted to browser time and adjusted for custom startOfDay if applicable),
    then the JavaScript Date library will init the Date object with the earlier timestamp of the two local datetime occurrences.
    This may cause getBeginningOfDay() to misbehave, so we need to adjust for it by getting the next hour. */
    if (beginningOfDay.getTime() === beginningOfNextDay.getTime()) {
        return getBeginningOfDayImpl(utcDate, toLocalTime, toUtcTime, numDaysOffset, true);
    }
    return beginningOfDay;
}

function getBeginningOfDayImpl(
    utcDate: Date,
    toLocalTime: (date: Date) => Date,
    toUtcTime: (date: Date) => Date,
    numDaysOffset = 0,
    dstAdjust = false
): Date {
    let localDate = toLocalTime(utcDate);
    let startOfLocalDate = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate() + numDaysOffset, dstAdjust ? 1 : 0);
    return toUtcTime(startOfLocalDate);
}

export function getBeginningOfNextDay(utcDate: Date, toLocalTime: (date: Date) => Date, toUtcTime: (date: Date) => Date): Date {
    return getBeginningOfDay(utcDate, toLocalTime, toUtcTime, 1);
}

export const getTimeLeftInDay = (state: IState) =>
    getBeginningOfNextDay(state.nextDate, state.toLocalTime, state.toUtcTime).getTime() - state.nextDate.getTime();

export function doPeriodsOverlap(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
    return start1.getTime() < end2.getTime() && start2.getTime() < end1.getTime();
}

export function getPeriodOverlap(start1: Date, end1: Date, start2: Date, end2: Date): undefined | { fromDate: Date; toDate: Date } {
    const fromDate = Math.max(start1.getTime(), start2.getTime());
    const toDate = Math.min(end1.getTime(), end2.getTime());
    return fromDate < toDate ? { fromDate: new Date(fromDate), toDate: new Date(toDate) } : undefined;
}

export function getCycleCounts(cycle: ICycle, config: IHosRulesetConfig): number {
    var countDriveOnly =
        config.cycle && config.cycle.cycleStatuses && config.cycle.cycleStatuses.length === 1 && config.cycle.cycleStatuses[0] === "D";
    if (countDriveOnly) {
        // custom scenario for saleperson
        return cycle.driveCount;
    }
    return cycle.onDutyCount;
}

/**
 * Adds up all the statuses applying to the driver's cycle and assigns it to each day
 */
export function getCycleRecapAndCycleAvailabilities(state: IState, getAllRecaps = false) {
    let result: { hosRecap: IHosRecap[]; hosCycleAvailabilities: IHosCycleAvailability[] | undefined } = {
        hosRecap: [],
        hosCycleAvailabilities: undefined
    };
    if (state.cycle.days === 0) {
        return result;
    }
    let countDriveOnly =
        state.config.cycle &&
        state.config.cycle.cycleStatuses &&
        state.config.cycle.cycleStatuses.length === 1 &&
        state.config.cycle.cycleStatuses[0] === "D";
    let cycleDays = state.config.cycle?.cycleDays;
    let isNonRolling = !!state.config.cycle?.nonRolling;
    if (!cycleDays) {
        return result;
    }

    let l = Math.max(0, getAllRecaps ? 0 : state.days.length - (state.cycle.days + (isNonRolling ? 0 : cycleDays)));
    let cycleRecapDays: ICycleDayRecap[] = state.days.slice(l, state.days.length).map(day => {
        return { dateTime: day.date, duration: countDriveOnly ? day.driveCount : day.onDutyCount };
    });
    let cycleMax = state.cycle.cycleMaximum || state.config.cycle?.cycleMaximum || 0;

    // sliding window to calculate cycleTotal and cycleRemaining for each day. Only days in the same cycle window as the day count towards the total.
    // For non-rolling cycles, skip subtraction — hours accumulate from the last restart without falling off.
    let r = 0;
    let cycleTotal = 0;
    while (r < cycleRecapDays.length) {
        let day = cycleRecapDays[r];
        cycleTotal += day.duration;
        if (!isNonRolling && r >= cycleDays) {
            cycleTotal -= cycleRecapDays[r - cycleDays].duration;
        }
        day.cycleTotal = cycleTotal;
        if (cycleMax > 0) {
            day.cycleRemaining = cycleMax - cycleTotal;
        }
        r++;
    }
    if (!getAllRecaps) {
        cycleRecapDays = cycleRecapDays.slice(-state.cycle.days);
    }
    let hosRecap: IHosRecap[] = cycleRecapDays.map(day => {
        let recap: IHosRecap = { dateTime: day.dateTime, duration: msToTimespan(day.duration) };
        if (day.cycleTotal !== undefined) {
            recap.cycleTotal = msToTimespan(day.cycleTotal);
        }
        if (day.cycleRemaining !== undefined) {
            recap.cycleRemaining = msToTimespan(day.cycleRemaining);
        }
        return recap;
    });
    return {
        hosRecap,
        hosCycleAvailabilities: isNonRolling
            ? getNonRollingCycleAvailabilities(state, cycleMax)
            : getCycleAvailabilities(state, cycleRecapDays)
    };
}

function getNonRollingCycleAvailabilities(state: IState, cycleMaximum: number): IHosCycleAvailability[] | undefined {
    if (!cycleMaximum) return undefined;
    let cycleDays = state.config.cycle?.cycleDays;
    // Non-rolling cycle expires after cycleDays without a restart.
    let currentAvailable = (cycleDays && state.cycle.days > cycleDays)
        ? 0
        : Math.max(0, cycleMaximum - getCycleCounts(state.cycle, state.config));
    let lastDay = state.days[state.days.length - 1];
    if (!lastDay) return undefined;

    let tomorrow = new Date(lastDay.date.getTime());
    tomorrow.setDate(tomorrow.getDate() + 1);

    return [
        { dateTime: tomorrow, available: msToTimespan(currentAvailable), gain: msToTimespan(0) }
    ];
}

function getCycleAvailabilities(state: IState, cycleRecap: ICycleDayRecap[]): IHosCycleAvailability[] | undefined {
    let cycleDays = state.config.cycle?.cycleDays;
    let cycleMaximum = state.cycle.cycleMaximum || state.config.cycle?.cycleMaximum;
    if (!cycleRecap || cycleRecap.length === 0 || !cycleDays || !cycleMaximum) {
        return undefined;
    }
    let recapDurations = cycleRecap.map(r => r.duration);
    const emptyRecapCount = cycleDays - recapDurations.length;
    if (emptyRecapCount > 0) {
        recapDurations = new Array(emptyRecapCount).fill(0).concat(recapDurations);
    }

    let cycleAvailabilities: { dateTime: Date; available: number; diff?: number }[] = [];
    let dateTime = new Date(cycleRecap[cycleRecap.length - 1].dateTime.getTime());
    let totalCurrentCycle = recapDurations.reduce((total, duration) => total + duration);

    // push a dummy value for the current day. This element is later removed before returning
    cycleAvailabilities.push({ dateTime, available: Math.max(0, cycleMaximum - totalCurrentCycle) });

    for (let recapDuration of recapDurations) {
        dateTime = new Date(dateTime.getTime());
        dateTime.setDate(dateTime.getDate() + 1);
        totalCurrentCycle -= recapDuration;
        if (state.config.cycle?.cycleMaximum && state.cycle.toDate && dateTime.getTime() > state.cycle.toDate?.getTime()) {
            cycleMaximum = state.config.cycle.cycleMaximum;
        }
        let available = Math.max(0, cycleMaximum - totalCurrentCycle);
        cycleAvailabilities.push({ dateTime, available });
        if (totalCurrentCycle <= 0) {
            break;
        }
    }

    for (let i = 1; i < cycleAvailabilities.length; i++) {
        let cycleAvailability = cycleAvailabilities[i];
        let prevCycleAvailability = cycleAvailabilities[i - 1];
        cycleAvailability.diff = cycleAvailability.available - prevCycleAvailability.available;
    }

    // splice out the dummy value since it represents the current day
    return cycleAvailabilities.splice(1).map(a => {
        return {
            dateTime: a.dateTime,
            available: msToTimespan(a.available),
            gain: msToTimespan(a.diff ?? 0)
        };
    });
}

export function hasExemption(ruleSetId: string, useLegacyTexasShortHaulRulesets = false): boolean {
    let ruleSetName = getRuleSetIndexById(ruleSetId);
    if (AUSTRALIARULESETS.indexOf(ruleSetName) !== -1) {
        // Australian work diary rulesets are HOS exempt, but don't want them to force an annotated OFF log
        return false;
    }
    return !!getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleSetName]?.config?.exempt;
}

export function getLatestExemptionLog<T extends { status: string }>(exemptionLogs: T[], exemptionType: string): T | undefined {
    for (let k = exemptionLogs.length - 1, j = 0; k >= j; k--) {
        if (exemptionLogs[k].status === exemptionType) {
            return exemptionLogs[k];
        }
    }
    return undefined;
}

export function hasCustomStartOfDay(ruleSetId: string, useLegacyTexasShortHaulRulesets = false) {
    let ruleSet = getRuleSetById(ruleSetId, useLegacyTexasShortHaulRulesets);
    return !!(ruleSet && ruleSet.config.customStartOfDay);
}

/**
 * Rulesets whose UserHosRuleSet records can persist a non-default Start of Day. Keep in sync with
 * UserHosRuleSetBridge.IsStartOfDayAllowed (C#), which coerces every other ruleset's Start of Day to 00:00:00 on save.
 * Broader than `hasCustomStartOfDay`, which only controls whether a driver can edit the value in Drive.
 */
const START_OF_DAY_ALLOWED_RULESETS = new Set([
    "CanadaCycleOne",
    "CanadaCycleTwo",
    "CanadaCycleOneTeam",
    "CanadaCycleTwoTeam",
    "CanadaNorthOf60CycleOne",
    "CanadaNorthOf60CycleTwo",
    "CanadaNorthOf60CycleOneTeam",
    "CanadaNorthOf60CycleTwoTeam",
    "CanadaOil",
    "CanadaOilTeam",
    "CanadaBCLoggingTruck"
]);

export function isStartOfDayAllowed(ruleSetId: string) {
    return START_OF_DAY_ALLOWED_RULESETS.has(ruleSetId.replace("HosRuleSet", ""));
}

export function hasResetPreviousCycle(ruleSetId: string, useLegacyTexasShortHaulRulesets = false) {
    let ruleSet = getRuleSetById(ruleSetId, useLegacyTexasShortHaulRulesets);
    return !!(ruleSet && ruleSet.config.resetPreviousCycle);
}

export function getFilteredRuleSets(useLegacyTexasShortHaulRulesets = false) {
    let ruleSetExp: IIdDictionary<IHosRuleset> = {};
    let ruleSets = getEffectiveRuleSets(useLegacyTexasShortHaulRulesets);
    for (let id in ruleSets) {
        if (ruleSets.hasOwnProperty(id)) {
            ruleSetExp[id] = ruleSets[id];
        }
    }
    return ruleSetExp;
}

export class UserHosRuleSet implements IUserHosRuleSet {
    constructor(userRuleSet?: IUserHosRuleSet | string | null, startOfDay?: string, userId?: string) {
        if (typeof userRuleSet === "string") {
            if (userRuleSet) {
                this.hosRuleSet = {
                    id: userRuleSet
                };
            }
        } else if (userRuleSet) {
            this.id = userRuleSet.id;
            this.user = userRuleSet.user ? { ...userRuleSet.user } : undefined;
            this.dateTime = userRuleSet.dateTime;
            this.hosRuleSet = { ...userRuleSet.hosRuleSet };
            this.startOfDay = userRuleSet.startOfDay;
        }
        if (userId) {
            this.user = { id: userId };
        }
        if (startOfDay && !this.startOfDay && hasCustomStartOfDay(getRuleSetIndexById(this.hosRuleSet.id))) {
            this.startOfDay = startOfDay || "00:00:00";
        }
        if (hasCustomStartOfDay(getRuleSetIndexById(this.hosRuleSet.id))) {
            this.startOfDay = startOfDay || this.startOfDay || "00:00:00";
        }
    }

    id?: string;
    user?;
    dateTime: Date | string = new Date();
    hosRuleSet = {
        id: "HosRuleSetNone"
    };
    startOfDay?: string;

    toJSON(): IUserHosRuleSet {
        return {
            id: this.id,
            user: { ...this.user },
            hosRuleSet: { ...this.hosRuleSet },
            dateTime: this.dateTime,
            startOfDay: this.startOfDay
        };
    }
}

let invertDictionary = function (ruleSetDictionary: IIdDictionary<string>): IIdDictionary<string> {
    var result: IIdDictionary<string> = {};
    for (var prop in ruleSetDictionary) {
        if (ruleSetDictionary.hasOwnProperty(prop)) {
            result[ruleSetDictionary[prop]] = prop;
        }
    }
    return result;
};

export function isRuleSetChanged(ruleSet1: IUserHosRuleSet, ruleSet2: IUserHosRuleSet): boolean {
    let ruleSet1Id = ruleSet1 && ruleSet1.hosRuleSet.id;
    let ruleSet2Id = ruleSet2 && ruleSet2.hosRuleSet.id;
    return !(ruleSet1Id === ruleSet2Id && ruleSet1.startOfDay === ruleSet2.startOfDay);
}

export function isOilTransportAllowed(ruleSet: string): boolean {
    return ruleSet in rulesetToOilTransportDictionary || ruleSet in oilTransportToRulesetDictionary;
}

export function isOilWellAllowed(ruleSet: string): boolean {
    return ruleSet in rulesetToOilWellDictionary || ruleSet in oilWellToRulesetDictionary;
}

export function isOilTransportSelected(ruleSet: string): boolean {
    return typeof ruleSet === "string" && ruleSet.indexOf("OilTransport") !== -1;
}

export function oilTransportToRuleSet(ruleSet: string) {
    // i.e. OilTransport7Day
    return isOilTransportSelected(ruleSet) ? oilTransportToRulesetDictionary[ruleSet] : ruleSet;
}

export function isOilWellSelected(ruleSet: string): boolean {
    return typeof ruleSet === "string" && ruleSet.indexOf("OilWell") !== -1;
}

export function oilWellToRuleSet(ruleSet: string) {
    // i.e. OilWell7Day
    return isOilWellSelected(ruleSet) ? oilWellToRulesetDictionary[ruleSet] : ruleSet;
}

// shortString is from localizerStrings
export var defPhrases: { [key: string]: IViolationPhrasesDict } = {
    cycle: { shortString: "HourCycleOnDutyLimitViolation", longString: "Maximum {0} hour(s) on duty in {1} days violation" },
    cycleDriving: { shortString: "HourCycleDrivingLimitViolation", longString: "Maximum {0} hour(s) driving in {1} days violation" },
    rest: { shortString: "HourRestLimitViolation", longString: "Driving without required break after {0}-hour driving violation" },
    dailyRest: { shortString: "HourDailyRestLimitViolation", longString: "Minimum {0}-hour rest per day violation" },
    driving: { shortString: "HourDrivingLimitViolation", longString: "Maximum {0}-hour driving limit violation" },
    dailyDriving: { shortString: "HourDailyDrivingLimitViolation", longString: "Maximum {0} hour(s) driving per day violation" },
    duty: { shortString: "HourOnDutyLimitViolation", longString: "Maximum {0}-hour on-duty limit violation" },
    dailyDuty: { shortString: "HourDailyOnDutyLimitViolation", longString: "Maximum {0} hour(s) on-duty per day violation" },
    dailyOff: { shortString: "HourDailyOffLimitViolation", longString: "Minimum {0}-hour off-duty per day violation" },
    workday: { shortString: "HourWorkdayLimitViolation", longString: "Maximum {0}-hour workday limit violation" },
    dutySinceCycleRest: { shortString: "DutySinceCycleRestViolation", longString: "Maximum {0} hour(s) on-duty since cycle rest violation." },
    cycleRest: {
        shortString: "CycleRestViolation",
        longString: "Driving without {0} period(s) of {1}-hour consecutive off-duty in the preceding {2} days."
    },
    australiaWorkHours: {
        shortString: "AustraliaWorkViolationHours",
        longString: "Work time violation. Exceeded the Work limit for the period of {0} hours."
    },
    australiaWorkDays: {
        shortString: "AustraliaWorkViolationDays",
        longString: "Work time violation. Exceeded the Work limit for the period of {0} days."
    },
    australiaRestHours: { shortString: "AustraliaRestViolationHours", longString: "Rest violation. Insufficient Rest for the period of {0} hours." },
    australiaRestDays: { shortString: "AustraliaRestViolationDays", longString: "Rest violation. Insufficient Rest for the period of {0} days." },
    australiaWorkExemption: { shortString: "AustraliaWorkExemptionViolation", longString: "Work Exemption violation" },
    cycleEnd: { shortString: "CycleEndViolation", longString: "Driving after {0}-day cycle period expiration violation" }
};

function getPeriodLimit(violation: IViolation) {
    let timePeriodString = "0";
    let type: "hours" | "days" = violation.hoursLimit ? "hours" : "days";

    if (violation.hoursLimit) {
        let hours = Math.floor(violation.hoursLimit);
        let halfHour = "";
        if ((violation.hoursLimit * 60) % 60 === 30) {
            halfHour = " 1/2";
        }
        timePeriodString = `${hours}${halfHour}`;
    } else if (violation.daysLimit) {
        timePeriodString = `${violation.daysLimit}`;
    }

    return [timePeriodString, type];
}

// eslint-disable-next-line complexity
export function populateViolationReason(violation: IViolation, translate: (s: string) => string = (s: string) => s, useShortString = false): string {
    const periodsLimit = violation.periodsLimit || "";
    const hoursLimit = violation.hoursLimit || "";
    const daysLimit = violation.daysLimit || "";
    const [periodLimitString, periodType] = getPeriodLimit(violation);
    let getString = (violationType: IViolationPhrasesDict) => violationType[useShortString ? "shortString" : "longString"];

    switch (violation.type) {
        case "Cycle":
            return translate(getString(defPhrases.cycle)).replace("{0}", `${hoursLimit}`).replace("{1}", `${daysLimit}`);
        case "Workday":
            return translate(getString(defPhrases.workday)).replace("{0}", `${hoursLimit}`);
        case "Duty":
            return translate(getString(defPhrases.duty)).replace("{0}", `${hoursLimit}`);
        case "DailyDuty":
            return translate(getString(defPhrases.dailyDuty)).replace("{0}", `${hoursLimit}`);
        case "Driving":
            return translate(getString(defPhrases.driving)).replace("{0}", `${hoursLimit}`);
        case "DailyDriving":
            return translate(getString(defPhrases.dailyDriving)).replace("{0}", `${hoursLimit}`);
        case "Rest":
            return translate(getString(defPhrases.rest)).replace("{0}", `${hoursLimit}`);
        case "DailyRest":
            return translate(getString(defPhrases.dailyRest)).replace("{0}", `${hoursLimit}`);
        case "DailyOff":
            return translate(getString(defPhrases.dailyOff)).replace("{0}", `${hoursLimit}`);
        case "DutySinceCycleRest":
            return translate(getString(defPhrases.dutySinceCycleRest)).replace("{0}", `${hoursLimit}`);
        case "CycleRest":
            return translate(getString(defPhrases.cycleRest))
                .replace("{0}", `${periodsLimit}`)
                .replace("{1}", `${hoursLimit}`)
                .replace("{2}", `${daysLimit}`);
        case "EwdRest":
            return translate(getString(periodType === "hours" ? defPhrases.australiaRestHours : defPhrases.australiaRestDays)).replace(
                "{0}",
                periodLimitString
            );
        case "EwdWork":
            return translate(getString(periodType === "hours" ? defPhrases.australiaWorkHours : defPhrases.australiaWorkDays)).replace(
                "{0}",
                periodLimitString
            );
        case "EwdWorkExemption":
            return translate(getString(defPhrases.australiaWorkExemption));
        case "CycleDriving":
            return translate(getString(defPhrases.cycleDriving)).replace("{0}", `${hoursLimit}`).replace("{1}", `${daysLimit}`);
        case "CycleEnd":
            return translate(getString(defPhrases.cycleEnd)).replace("{0}", `${daysLimit}`);
        default:
            return "";
    }
}

var rulesetToOilTransportDictionary: { [baseRuleSet: string]: string } = {
    HosRuleSetAmerica7Day: "HosRuleSetOilTransport7Day",
    HosRuleSetAmerica7DayBig: "HosRuleSetOilTransport7DayBig",
    HosRuleSetAmerica8Day: "HosRuleSetOilTransport8Day",
    HosRuleSetAmerica8DayBig: "HosRuleSetOilTransport8DayBig",
    HosRuleSetAmericaShortHaul: "HosRuleSetOilTransportShortHaul",
    HosRuleSetAmericaShortHaul8Day: "HosRuleSetOilTransportShortHaul8Day",
    HosRuleSetAmericaShortHaul14hrWorkday: "HosRuleSetOilTransportShortHaul14hrWorkday",
    HosRuleSetAmericaShortHaul8Day14hrWorkday: "HosRuleSetOilTransportShortHaul8Day14hrWorkday",
    HosRuleSetAmericaTexas: "HosRuleSetOilTransportTexas",
    HosRuleSetAmericaNoRestRequirement7Day: "HosRuleSetOilTransportNoRestRequirement7Day",
    HosRuleSetAmericaNoRestRequirement7DayBig: "HosRuleSetOilTransportNoRestRequirement7DayBig",
    HosRuleSetAmericaNoRestRequirement8Day: "HosRuleSetOilTransportNoRestRequirement8Day",
    HosRuleSetAmericaNoRestRequirement8DayBig: "HosRuleSetOilTransportNoRestRequirement8DayBig",
    HosRuleSetCalifornia8day: "HosRuleSetOilTransportCalifornia8day",
    HosRuleSetCaliforniaProperty: "HosRuleSetOilTransportCaliforniaProperty",
    HosRuleSetAmerica7DaySleeper: "HosRuleSetOilTransport7DaySleeper",
    HosRuleSetAmerica7DayBigSleeper: "HosRuleSetOilTransport7DayBigSleeper",
    HosRuleSetAmerica8DaySleeper: "HosRuleSetOilTransport8DaySleeper",
    HosRuleSetAmerica8DayBigSleeper: "HosRuleSetOilTransport8DayBigSleeper",
    HosRuleSetAmericaNoRestRequirement7DaySleeper: "HosRuleSetOilTransportNoRestRequirement7DaySleeper",
    HosRuleSetAmericaNoRestRequirement7DayBigSleeper: "HosRuleSetOilTransportNoRestRequirement7DayBigSleeper",
    HosRuleSetAmericaNoRestRequirement8DaySleeper: "HosRuleSetOilTransportNoRestRequirement8DaySleeper",
    HosRuleSetAmericaNoRestRequirement8DayBigSleeper: "HosRuleSetOilTransportNoRestRequirement8DayBigSleeper"
};
var oilTransportToRulesetDictionary = invertDictionary(rulesetToOilTransportDictionary);

var rulesetToOilWellDictionary: { [baseRuleSet: string]: string } = {
    HosRuleSetAmerica7Day: "HosRuleSetOilWell7Day",
    HosRuleSetAmerica7DayBig: "HosRuleSetOilWell7DayBig",
    HosRuleSetAmerica8Day: "HosRuleSetOilWell8Day",
    HosRuleSetAmerica8DayBig: "HosRuleSetOilWell8DayBig",
    HosRuleSetAmericaTexas: "HosRuleSetOilWellTexas",
    HosRuleSetAmericaNoRestRequirement7Day: "HosRuleSetOilWellNoRestRequirement7Day",
    HosRuleSetAmericaNoRestRequirement7DayBig: "HosRuleSetOilWellNoRestRequirement7DayBig",
    HosRuleSetAmericaNoRestRequirement8Day: "HosRuleSetOilWellNoRestRequirement8Day",
    HosRuleSetAmericaNoRestRequirement8DayBig: "HosRuleSetOilWellNoRestRequirement8DayBig",
    HosRuleSetCalifornia8day: "HosRuleSetOilWellCalifornia8day",
    HosRuleSetCaliforniaProperty: "HosRuleSetOilWellCaliforniaProperty",
    HosRuleSetAmerica7DaySleeper: "HosRuleSetOilWell7DaySleeper",
    HosRuleSetAmerica7DayBigSleeper: "HosRuleSetOilWell7DayBigSleeper",
    HosRuleSetAmerica8DaySleeper: "HosRuleSetOilWell8DaySleeper",
    HosRuleSetAmerica8DayBigSleeper: "HosRuleSetOilWell8DayBigSleeper",
    HosRuleSetAmericaNoRestRequirement7DaySleeper: "HosRuleSetOilWellNoRestRequirement7DaySleeper",
    HosRuleSetAmericaNoRestRequirement7DayBigSleeper: "HosRuleSetOilWellNoRestRequirement7DayBigSleeper",
    HosRuleSetAmericaNoRestRequirement8DaySleeper: "HosRuleSetOilWellNoRestRequirement8DaySleeper",
    HosRuleSetAmericaNoRestRequirement8DayBigSleeper: "HosRuleSetOilWellNoRestRequirement8DayBigSleeper"
};
var oilWellToRulesetDictionary = invertDictionary(rulesetToOilWellDictionary);

export function invertPropertyRulesetAndOilTransport(ruleSet: string) {
    if (isOilTransportSelected(ruleSet)) {
        // i.e. OilTransport7Day => America7Day
        return oilTransportToRulesetDictionary[ruleSet];
    }

    if (isOilTransportAllowed(ruleSet)) {
        // i.e. America7Day => OilTransport7Day
        return rulesetToOilTransportDictionary[ruleSet];
    }

    return ruleSet;
}

export function invertPropertyRulesetAndOilWell(ruleSet: string) {
    if (isOilWellSelected(ruleSet)) {
        // i.e. OilWell7Day => America7Day
        return oilWellToRulesetDictionary[ruleSet];
    }

    if (isOilTransportAllowed(ruleSet)) {
        // i.e. America7Day => OilWell7Day
        return rulesetToOilWellDictionary[ruleSet];
    }

    return ruleSet;
}

export function getRuleSets(useLegacyTexasShortHaulRulesets = false) {
    let ruleSetExp = {} as IIdDictionary<string>;
    let ruleSets = getEffectiveRuleSets(useLegacyTexasShortHaulRulesets);
    for (let id in ruleSets) {
        if (ruleSets.hasOwnProperty(id)) {
            ruleSetExp[id] = ruleSets[id].name;
        }
    }
    return ruleSetExp;
}

export function getAllRuleSets(useLegacyTexasShortHaulRulesets = false) {
    return getEffectiveRuleSets(useLegacyTexasShortHaulRulesets);
}

export function getRawRuleSet(ruleSetIndex: string, useLegacyTexasShortHaulRulesets = false) {
    return getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleSetIndex];
}

export function recreateRuleSetsList(ruleSets: IIdDictionary<IHosRuleset>): IHosRuleset[] {
    const ruleSetsList = [] as any;
    for (let key in ruleSets) {
        let ruleSet = ruleSets[key];
        ruleSet.id = key;
        ruleSetsList.push(ruleSet);
    }
    return ruleSetsList;
}

interface IHosLogPredicateInput {
    status: string;
    state?: string;
    isTransitioning?: boolean;
    driver?: IIdEntity | "NoUserId" | null;
    requestedEdit?: {
        state?: string;
        driver?: IIdEntity | "NoUserId" | null;
    };
    modified?: string;
    annotations?: readonly unknown[];
}

export function isRequestedAdd(log: IHosLogPredicateInput): boolean {
    return log.state === "Requested";
}

function isAwaitingTransition(log: IHosLogPredicateInput): boolean | undefined {
    return log.isTransitioning;
}

function isUnidentified(log: IHosLogPredicateInput): boolean {
    return (
        !log.driver ||
        (typeof log.driver !== "string" && log.driver.id === "NoUserId") ||
        (!!log.requestedEdit &&
            log.requestedEdit.state === "Active" &&
            !!log.requestedEdit.driver &&
            typeof log.requestedEdit.driver !== "string" &&
            log.requestedEdit.driver.id === "NoUserId")
    );
}

function isRemoved(log: IHosLogPredicateInput): boolean {
    return log.modified === "Remove" || log.state === "Inactive" || log.state === "Rejected";
}

export function hasPendingRequest(log: IHosLogPredicateInput): boolean {
    return (
        isRequestedAdd(log) ||
        (!!log.requestedEdit &&
            !isAwaitingTransition(log) &&
            (log.requestedEdit.state === "Requested" || log.requestedEdit.state === "Transitioning"))
    );
}

export function isStandardStatusDriverLog(log: IHosLogPredicateInput): boolean {
    return !isUnidentified(log) && !isRemoved(log) && !isIntermediateLog(log) && !isMalfunctionLog(log) && !hasPendingRequest(log);
}

export function isMalfunctionLog(log: IHosLogPredicateInput): boolean {
    return MALFUNCTION_LOG_TYPES.indexOf(log.status) > -1;
}

export function isCertifyLog(log: IHosLogPredicateInput): boolean {
    return ["Certify"].indexOf(log.status) > -1;
}

export function isLoginLogoutLog(log: IHosLogPredicateInput): boolean {
    return ["Login", "Logoff"].indexOf(log.status) > -1;
}

export function hasAnnotation(log: IHosLogPredicateInput): boolean {
    return !!log.annotations && log.annotations.length > 0;
}

export function isCycleOrZoneChangeLog(log: IHosLogPredicateInput): boolean {
    return CANADIAN_COMPLIANCE_LOG_TYPES.some(status => status === log.status);
}

export function isControlLog(log: IHosLogPredicateInput): boolean {
    return CONTROL_LOG_TYPES.some(status => status === log.status) || log.status === "RuleSetChange";
}

export function isAuthorityLog(log: IHosLogPredicateInput): boolean {
    return AUTHORITY_LOG_TYPES.some(status => status === log.status);
}

export function isHosGraphLog(log: IHosLogPredicateInput, includeRequestedAdd?: boolean): boolean {
    return isStatusLog(log) && (includeRequestedAdd || !isRequestedAdd(log));
}

export function isStatusLog(log: IHosLogPredicateInput): boolean {
    return ALL_STATUS_LOG_TYPES.some(status => status === log.status);
}

export function isExemptionLog(log: IHosLogPredicateInput): boolean {
    return EXEMPTION_LOG_TYPES.some(status => status === log.status);
}

export function isEngineLog(log: IHosLogPredicateInput): boolean {
    return ENGINE_LOG_TYPES.some(status => status === log.status);
}

export function isAustralianStatusLog(log: IHosLogPredicateInput): boolean {
    return ALL_AUSTRALIAN_LOG_TYPES.some(status => status === log.status);
}

export function isIntermediateLog(log: IHosLogPredicateInput): boolean {
    return INTERMEDIATE_LOG_TYPES.some(status => status === log.status);
}

export function isSystemLog(log: IHosLogPredicateInput): boolean {
    const NON_SYSTEM_LOGS = ALL_STATUS_LOG_TYPES.concat(EXEMPTION_LOG_TYPES).concat(AUTHORITY_LOG_TYPES);
    if (!NON_SYSTEM_LOGS.some(status => status === log.status)) {
        return true;
    }
    return false;
}

const UNSUPPORTED_CANADA_ELD_LOG_TYPES: THosStatus[] = SYSTEM_STATUSES.concat([
    "Connected",
    "Disconnected",
    "WT",
    "Work",
    "Rest"
]);

export function isSupportedCanadaEldLog(log: IHosLogPredicateInput): boolean {
    return !UNSUPPORTED_CANADA_ELD_LOG_TYPES.some(status => status === log.status);
}

export function getRuleSetName(id: string, useLegacyTexasShortHaulRulesets = false) {
    let prefix = "HosRuleSet",
        rulesetKey = id;

    if (rulesetKey.indexOf(prefix) === 0) {
        rulesetKey = rulesetKey.replace(prefix, "");
    }

    let ruleSets = getEffectiveRuleSets(useLegacyTexasShortHaulRulesets);
    if (ruleSets[rulesetKey]) {
        return ruleSets[rulesetKey].name;
    }

    return rulesetKey;
}

export function getLogRetentionDays(ruleSet: string | null | undefined, useLegacyTexasShortHaulRulesets = false) {
    if (ruleSet) {
        let ruleSetConfig = getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleSet];
        return (ruleSetConfig && ruleSetConfig.config && ruleSetConfig.config.logRetentionDays) || MAX_REPORT_CYCLE_DAYS;
    }
    return MAX_REPORT_CYCLE_DAYS;
}

export function getCycleDays(ruleSet: string | IHosRuleset | undefined, useLegacyTexasShortHaulRulesets = false) {
    if (!ruleSet) {
        return MAX_REPORT_CYCLE_DAYS;
    }
    let ruleSetObj = typeof ruleSet === "string" ? getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleSet] : ruleSet;
    return ruleSetObj.config.cycle?.cycleDays;
}

export function getOperatingZoneFromRuleSet(ruleset: string | undefined): string {
    if (ruleset && ruleset.indexOf("CanadaNorthOf60") > -1) {
        return "2";
    }

    if (ruleset && (ruleset.indexOf("Canada") > -1 || ruleset.indexOf("Alberta") > -1)) {
        return "1";
    }

    if (ruleset) {
        return "3";
    }

    return "";
}

export function getWorkdayMaximumWithDate(ruleset: string | IHosRuleset, useLegacyTexasShortHaulRulesets = false): number {
    let rule = typeof ruleset === "string" ? getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleset] : ruleset,
        workday = 0;

    if (!rule) {
        return 0;
    }

    let config = rule.config;
    if (config.workday && config.workday.workdayMaximum) {
        workday = config.workday.workdayMaximum / HOURS;
    }

    return workday;
}

export function getDefaultAvailability(ruleset: string | IHosRuleset, availabilityPerTwoDays = false, useLegacyTexasShortHaulRulesets = false): IHosAvailability {
    let rule = typeof ruleset === "string" ? getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleset] : ruleset;
    let availability = {
        recap: []
    } as IHosAvailability;

    if (rule) {
        let config = rule.config;
        if (config.rest) {
            if (config.rest.betweenRestLimit) {
                availability.rest = msToTimespan(config.rest.betweenRestLimit);
            } else if (config.rest.dailyRestMinimum) {
                availability.rest = msToTimespan(24 * HOURS - config.rest.dailyRestMinimum);
            }
        }

        if (config.cycle) {
            if (config.cycle.cycleMaximum) {
                availability.cycle = msToTimespan(config.cycle.cycleMaximum);
            }
            if (config.cycle.cycleDrivingMaximum) {
                availability.cycleDriving = msToTimespan(config.cycle.cycleDrivingMaximum);
            }
            if (config.cycle.dutyMaximumSinceCycleRest) {
                availability.dutySinceCycleRest = msToTimespan(config.cycle.dutyMaximumSinceCycleRest);
            }
            if (config.cycle.cycleRestDays) {
                availability.cycleRest = msToTimespan(config.cycle.cycleRestDays * DAYS);
            }
        }

        if (config.workday) {
            if (config.workday.dutyMaximum) {
                availability.duty = msToTimespan(config.workday.dutyMaximum);
            }
            if (config.workday.workdayMaximum) {
                availability.workday = msToTimespan(config.workday.workdayMaximum);
            }
            if (config.workday.driveMaximum) {
                availability.driving = msToTimespan(config.workday.driveMaximum);
            }
        }

        //Get Daily availabilities
        getDefaultDailyAvailability(availability, config, availabilityPerTwoDays);
        return availability;
    }

    return availability;
}

export function getDefaultDailyAvailability(
    availability: IHosAvailability,
    config: IHosRulesetConfig,
    availabilityPerTwoDays = false
): IHosAvailability {
    if (!config.daily) {
        return availability;
    }
    const days = availabilityPerTwoDays ? 2 : 1;
    // Daily availabilities
    if (config.daily.driveMaximum) {
        availability.dailyDriving = msToTimespan(config.daily.driveMaximum * days);
    }
    if (config.daily.dutyMaximum) {
        availability.dailyDuty = msToTimespan(config.daily.dutyMaximum * days);
    }
    if (config.daily.offDutyMinimum) {
        availability.dailyOff = msToTimespan(config.daily.offDutyMinimum * days);
    }

    return availability;
}

export function getDefaultValidateResult(ruleset: string): IValidation {
    return {
        violations: [] as any[],
        availability: getDefaultAvailability(ruleset),
        cycleSummaries: []
    };
}

export function getControlLogTypes(): THosStatus[] {
    return CONTROL_LOG_TYPES;
}

export function getStatusLogTypes(): THosStatus[] {
    return STATUS_LOG_TYPES;
}

export function getCanadaRulesets(): string[] {
    return CANADARULESETS;
}

export interface ICycle {
    fromDate: Date;
    toDate?: Date;
    completedRestartDate?: Date;
    dutyCountSince24HourRestPeriod: number;
    cycleConsecutiveOffCount: number;
    driveCount: number;
    onDutyCount: number;
    days: number;
    bigDayCount: number;
    hasStarted: boolean;
    pauseCountingCycleOff: boolean;
    completedRestPeriodDates: Date[];
    cycleMaximum: number;
    cycleResetPeriod?: ICycleResetPeriod;
}

export interface IRestInfo {
    fromDate: Date;
    toDate: Date;
    isTemp?: boolean;
    isWt?: boolean;
}

export interface IDay {
    dateIndex: string;
    date: Date;
    driveCount: number;
    yesterdayDriveCount?: number;
    onDutyCount: number;
    offDutyCount: number;
    restCount?: number;
    restInfo?: IRestInfo[];
    statusCounts: { [index: string]: number };
    bigDayExemptionUsed?: boolean;
    deferralTime?: number;
    deferralStatus?: TDeferralStatus;
    deferralLogNeeded?: boolean;
    deferralLogRequired?: TDeferralStatus;
    consecutiveOff?: number;
    unCountedOff?: number;
    hoursInCycle: number;
    isRailroadLogApplied?: boolean;
    isAdverseLogApplied?: boolean;
}

export interface IHosRecap {
    dateTime: Date;
    duration: string;
    cycleTotal?: string;
    cycleRemaining?: string;
}

interface ICycleDayRecap {
    dateTime: Date;
    duration: number;
    cycleTotal?: number;
    cycleRemaining?: number;
}

export interface IHosCycleAvailability {
    dateTime: Date;
    available: string;
    gain?: string;
}

export interface IHosAvailability {
    driver?: any;
    rest?: string;
    restLabel?: string;
    driving?: string;
    workday?: string;
    duty?: string;
    cycle?: string;
    dailyDriving?: string;
    cycleDriving?: string;
    dailyDuty?: string;
    dailyRest?: string;
    dailyOff?: string;
    recap: IHosRecap[];
    cycleAvailabilities?: IHosCycleAvailability[];
    dutySinceCycleRest?: string;
    cycleRest?: string;
    drivingBreakDuration?: string;
    is16HourExemptionAvailable?: boolean;
    isAdverseDrivingExemptionAvailable?: boolean;
    isAdverseDrivingApplied?: boolean;
    isEmergencyResponseExemptionApplied?: boolean;
    isOffDutyDeferralExemptionAvailable?: boolean;
    isRailroadExemptionAvailable?: boolean;
    exemptionReducedRest?: {
        isAvailable: boolean;
        restAmountToBeDeferred: number;
    };
    isSplittingWorkday?: boolean;
}

export interface IHosPreciseAvailability {
    rest?: number;
    restLabel?: string;
    driving?: number;
    workday?: number;
    duty?: number;
    cycle?: number;
    dailyDriving?: number;
    cycleDriving?: number;
    dailyDuty?: number;
    dailyRest?: number;
    dailyOff?: number;
    dutySinceCycleRest?: number;
    cycleRest?: number;
    drivingBreakDuration?: number;
}

export interface IHosViolationSlim {
    reason: string;
    start: string;
}

export interface IHosOffDeferral {
    deferralStatus?: string;
    deferralMinutes?: number;
    isReset?: boolean;
}

export interface IEligibleReducedRestPeriod {
    start?: Date;
    end?: Date;
}

export interface IWorkday {
    hasStarted: boolean;
    consecutiveOffCount: number;
    consecutiveWtCount: number;
    consecutiveOffStartTime?: Date;
    /**
     * Start of the WT-free tail of the current consecutive off-duty period. Only that tail can supply
     * a workday reset's split candidate, so a WT segment earlier in the rest does not veto it.
     */
    splitEligibleConsecutiveOffStartTime?: Date;
    /** Duration (ms) of the contiguous sleeper-berth run the current consecutive off-duty period is in, if any. */
    consecutiveSleeperBerthCount?: number;
    /**
     * Longest contiguous sleeper-berth run (ms) inside the current consecutive off-duty period. A run that already
     * qualifies as the long split leg disqualifies the rest from carrying as the short leg, and the workday split
     * tracker that also records it is recreated the moment the rest reaches the workday minimum.
     */
    longestSleeperBerthInConsecutiveOff?: number;
    timeUntilMustRest?: number;
    consecutiveRestCount?: number;
    consecutiveRestStartTime?: Date;
    start?: Date;
    end?: Date;
    drivingLimit?: number;
    dutyLimit?: number;
    driveCount?: number;
    onDutyCount?: number;
    drivingExtended?: number;
    workdayExtended?: number;
    offDutyMinimum?: number;
    endOfLastBreak?: Date;
    splitSB?: ISplitInfo;
    splitLogs?: any[];
    propertySplitSB?: ISplitInfoProperty;
    lastSBPeriodMinimum?: number;
    completedSplitSB?: boolean;
    /** Duration (ms) of the completed pairing's later leg this workday was born excluding from
     *  its window — an EARNED extension, as opposed to a speculative freeze. MYG-133117. */
    earnedSplitExclusion?: number;
    /** Start of the live SB leg that consumed a carried reset candidate and can still grow its earned exclusion. */
    carriedResetPairingSleeperStart?: Date;
    /** True when earnedSplitExclusion belongs to the carried-reset pairing path rather than a replayed normal split. */
    earnedSplitExclusionFromCarriedResetPairing?: boolean;
    /** Reset metadata replaced on the prior workday while a carried pairing can still grow into a normal reset. */
    resetInfoBeforeCarriedResetPairing?: TWorkdayResetInfo;
    /** Distinguishes an originally absent resetInfo from no carried-pairing summary replacement. */
    replacedPreviousWorkdayResetInfoForCarriedPairing?: boolean;
    /**
     * A qualifying OFF-duty split period from a standard workday reset, retained until a later
     * sleeper-berth period consumes it or a subsequent workday reset supersedes it.
     */
    offDutySplitPeriodFromStandardWorkdayReset?: ISplitPeriod;
    isAdverseLogApplied?: boolean;
    is16HourExemptionApplied?: boolean;
    isExemptionReducedRestApplied?: boolean;
    endOfLastDLog?: Date;
    adverseLogStart?: Date;
    reducedRestUsed?: number;
    eligibleReducedRestPeriods?: IEligibleReducedRestPeriod[];
    resetInfo?: TWorkdayResetInfo;
    prevLastReducedRestApplyDate?: Date;
}

export interface IRuleSetDateRange {
    start: Date | null;
    end: Date | null;
}

export interface ICurrentViolationMap {
    [violationType: string]: IRawViolation;
}

export interface IState {
    config: IHosRulesetConfig;
    i: number;
    allocatedCountIndex: number;
    current: any;
    next: any;
    currentDate: Date;
    nextDate: Date;
    duration: number;
    currentDateIndex: string;
    nextDateIndex: string;
    currentViolations: ICurrentViolationMap;
    pastViolations: IRawViolation[];
    workday: IWorkday;
    day: IDay;
    cycle: ICycle;
    workdays: IWorkday[];
    days: IDay[];
    cycles: ICycle[];
    logs: any[];
    exemption16HLogs: any[];
    adverseLogs: any[];
    railroadLogs: any[];
    // Emergency Response exemption control logs (MYG-139401). Persisted across incremental
    // calculations and pruned on the same retention cutoff as the rest of the state, because
    // previous days' daily violations are re-derived from them on every calculation.
    emergencyResponseLogs: any[];
    // Derived from emergencyResponseLogs once per calculation and never mutated afterwards, so
    // the split-sleeper log rewind cannot corrupt it. Not serialised — rebuilt every call.
    emergencyResponsePeriods: IEmergencyResponsePeriod[];
    // Violation types whose record the exemption cut short while the driver was still past the
    // limit (MYG-139401). currentViolations is both the record and the engine's "past this limit"
    // flag; closing the record clears the flag, so the flag is kept here instead. Persisted,
    // because an incremental calculation must reach the same answer as a single-shot one.
    emergencyResponseSuppressedViolations: TViolationType[];
    reducedRestLogs: { [index: string]: any };
    deferralLogs: { [index: string]: any };
    rulesetDateRanges: IRuleSetDateRange[];
    isInit: boolean;
    splitSBDuringReset: number | undefined;
    toLocalTime: (date: Date) => Date;
    toUtcTime: (date: Date) => Date;
    propertySplitSubConfig?: IPropertySplitSubConfig;
    intermediateSplitConfig: IIntermediateSplitConfig;
    lastReducedRestApplyDate?: Date;
    cycleRestartDatesFromOtherRulesets?: Date[];
    // Sleeper periods that already completed a pairing with a small split period behind them. Survives the
    // log rewind a completed split triggers, so the same sleeper cannot pair backward a second time.
    sleeperStartsPairedWithAnEarlierSmallSplit?: number[];
    customCodes: ICustomCodeState
}

export interface ICustomCodeState {
    enableAutoCreateNoneDeferralLog: boolean,
    enablePreventSplitCompleteWhenViolationFix: boolean,
    disableRemoveOrTrimViolationsAfter16hException: boolean,
    disableUnfreezeSBSplitNotCompleteFix: boolean,
    disableExemptionLogsInteractionRefactor: boolean,
    disableOngoingViolationRulesetFix: boolean,
    disableCycleRestartUsedInFollowingValidations: boolean,
    disablePrevCycleRestDurationCountingFix: boolean,
    disableDeferralRestSubtractionFix: boolean,
    disableErrRestReconciliationFix: boolean,
    disableDstDailyAvailabilityFix: boolean,
    disableUsaPropertySplitPairingReuseFix: boolean,
    disableUsaPropertyResetSplitPairingFix: boolean,
    enableUsaPreventWorkdayResetPairingForward: boolean,
    disableNonSplitAvailability: boolean,
    disableTexasShortHaulRulesetCorrection: boolean,
    disableCycleResetPeriod: boolean,
    enableEmergencyResponseExemption: boolean
}

export const DEFAULT_CUSTOM_CODE_STATE: ICustomCodeState = {
    enableAutoCreateNoneDeferralLog: false,
    enablePreventSplitCompleteWhenViolationFix: false,
    disableRemoveOrTrimViolationsAfter16hException: false,
    disableUnfreezeSBSplitNotCompleteFix: false,
    disableExemptionLogsInteractionRefactor: false,
    disableOngoingViolationRulesetFix: false,
    disableCycleRestartUsedInFollowingValidations: false,
    disablePrevCycleRestDurationCountingFix: false,
    disableDeferralRestSubtractionFix: false,
    disableErrRestReconciliationFix: false,
    disableDstDailyAvailabilityFix: false,
    disableUsaPropertySplitPairingReuseFix: false,
    disableUsaPropertyResetSplitPairingFix: false,
    enableUsaPreventWorkdayResetPairingForward: false,
    disableNonSplitAvailability: false,
    disableTexasShortHaulRulesetCorrection: false,
    disableCycleResetPeriod: false,
    enableEmergencyResponseExemption: false
};

/** The complete set of custom-code inputs accepted by the HOS calculator. */
export const CUSTOM_CODE_NAMES = Object.freeze(Object.keys(DEFAULT_CUSTOM_CODE_STATE) as Array<keyof ICustomCodeState>);

export interface ISplitInfo {
    inSleeper: boolean;
    periods: ISplitPeriod[];
}

export interface ISplitInfoProperty {
    inSleeper: boolean;
    inOffDuty: boolean;
    sbPeriods: ISplitPeriod[];
    offDutyPeriods: ISplitPeriod[];
}

export interface ISplitPeriod {
    fromDate: Date;
    toDate: Date;
}

export interface IDeferralInfo {
    time: string;
    status: TDeferralStatus;
    deferralLogRequired?: boolean;
    requiredDeferralLogStatus?: TDeferralStatus;
}

export interface IDaySummary {
    dateTime: Date;
    onTotal?: string;
    offTotal?: string;
    driveTotal?: string;
    sleeperBerthTotal?: string;
    personalConveyanceTotal?: string;
    yardMoveTotal?: string;
    waitTimeTotal?: string;
    deferralInfo?: IDeferralInfo;
    restInfos?: IRestInfo[];
    hoursInCycle?: string;
}

export interface IWorkdaySummary {
    fromDate?: Date;
    toDate?: Date;
    driveExtension?: string;
    workdayExtension?: string;
    driveCount?: string;
    onDutyCount?: string;
    onDutyIntervals?: {start: Date; end: Date}[];
    isInProgress?: boolean;
    resetInfo?: TWorkdayResetInfo;
}

export interface ICycleResetPeriod {
    fromDate: Date;
    eligibleForResetDate: Date;
    toDate?: Date;
}

export interface ICycleSummary {
    dateTime: Date;
    consecutiveRest: string;
    cycleResetPeriod?: ICycleResetPeriod;
}

export interface ICalculationResult {
    validation: IValidation;
    state: IState | undefined;
}

export interface IValidation {
    violations: IViolation[];
    availability: IHosAvailability;
    preciseAvailability?: IHosPreciseAvailability;
    /** MYG-133117: availability without the current workday's in-progress split sleeper freeze (completed splits preserved) */
    nonSplitAvailability?: IHosAvailability;
    nonSplitPreciseAvailability?: IHosPreciseAvailability;
    nonSplitViolations?: IViolation[];
    discreteAvailability?: TDiscreteAvailabilitySummary;
    ewdAvailability?: IEwdWorkRestAvailability[];
    cycleSummaries: ICycleSummary[];
    restBreakNeeded?: string;
    offDutyNeeded?: string;
    daySummaries?: IDaySummary[];
    workdaySummaries?: IWorkdaySummary[];
    rulesetsThatCannotChange?: IUserHosRuleSet[];
    currentDutyStatus?: THosStatus;
    remainingSplitDuration?: number;
    nextHosEventDate?: Date;
    nextAvailabilityEventDates?: TNextAvailabilityEventDate;
}

export interface INextAvailabilityEvent {
    isConsuming: boolean;
    /** When availability would reach zero if current activity continues */
    nextExhaustionEventDate?: Date;
    /** When availability would change (e.g., reset at end of day, break qualifies) */
    nextAvailabilityEventDate?: Date;
}

export type TNextAvailabilityEventDate = { [violationType in TViolationType]?: INextAvailabilityEvent | undefined };

export interface IValidateStateAndResult {
    validation: IValidation;
    stateByRuleset?: IValidateStateByRuleset;
}

export interface IValidateStateByRuleset {
    [id: string]: IState | undefined;
}

// Following interface in used just inside ewdCalculator
export interface IEwdState {
    logs: any[];
    config: IHosRulesetConfig;
    timePeriodList: number[];
    current: any;
    next: any;
    currentDate: Date;
    nextDate: Date;
    duration: number;
    violations: IViolation[];
    workRestCounts: { [index: string]: IWorkRestCount };
    lastExemptionDate: Date;
    lastNightRestBreakDate: Date;
    workExemptionCount: number;
    consecutiveRestCount: number;
}

export interface IWorkRestCount {
    startDate: Date;
    toDate: Date;
    workPeriod: IEwdWorkPeriod | undefined;
    restPeriod: IEwdRestPeriod | undefined;
}

export interface IEwdWorkPeriod {
    workCount?: number;
    workMaximum?: number;
    // TODO:longNightWork is any work time (outside of the period midnight to 6.00 am) that is in excess of 12 hours of work in a 24 hour
    // period or any work time between midnight and 6 am (or the equivalent hours in the Timezone of the base of a driver).
    longNightWork?: number;
}

export interface IRestSegment {
    startDate: Date;
    toDate: Date;
}

export interface IEwdRestPeriod {
    restSegments?: IRestSegment[];
    restMinimum?: number;
    countRequired?: number; // number of restMinimum required to complete for this timePeriod
    // nightRestBreak are 7 continuous hours stationary rest time taken between the hours of 10pm on a day and 8am on the next day
    // (using the Timezone of the base of the driver) or a 24 continuous hours stationary rest break.
    nightRestBreakCount?: number;
    consecutiveNightRestBreakCount?: number; // nightRestBreak on consecutive day
    nightRestBreakCountRequired?: number;
    consecutiveNightRestBreakCountRequired?: number; // nightRestBreak on consecutive day
}

export interface IMinAvailability {
    min: number;
    name?: string;
}

export interface IPropertySplitSubConfig {
    smallSplitMin: number;
    bigSplitMin: number;
    bigSplitMax?: number; // when defined, the big split period duration must be strictly less than this number to be eligible for split
    onlyFreezeBigPeriods?: boolean; // when this is true, only the big split period does not count against the driving window
}

function pad(input: string, size: number): string {
    let temp = input;
    while (temp.length < size) {
        temp = "0" + temp;
    }
    return temp;
}

export function msToTimespan(ms: number, allowNegative = false): string {
    if (!allowNegative && ms <= 0) {
        return "00:00:00";
    }
    let seconds = ms / 1000;
    let minutes = seconds / 60;
    let hours = minutes / 60;
    let days = hours / 24;
    let rMs = ~~(ms % 1000);
    let rSeconds = ~~(seconds % 60);
    let rMinutes = ~~(minutes % 60);
    let rHours = ~~(hours % 24);
    let rDays = ~~days;
    let daysString = rDays === 0 ? "" : `${rDays.toString()}.`;
    let msString = rMs === 0 ? "" : `.${pad(rMs.toString(), 3)}`;
    return `${daysString}${pad(rHours.toString(), 2)}:${pad(rMinutes.toString(), 2)}:${pad(rSeconds.toString(), 2)}${msString}`;
}

export function msToShortTimespan(ms: number, hoursPadding = 2): string {
    if (ms <= 0) {
        return `${pad("0", hoursPadding)}:00`;
    }
    let minutes = ms / 1000 / 60;
    let hours = ~~(minutes / 60);
    let rMinutes = ~~(minutes % 60);
    return `${pad(hours.toString(), hoursPadding)}:${pad(rMinutes.toString(), 2)}`;
}

export function timeSpanToMs(timeSpan: string | { toString(): string } | undefined): number {
    let days = 0;
    let temp = typeof timeSpan === "string" ? timeSpan : (timeSpan as { toString(): string }).toString();

    let isNeg = temp.charAt(0) === "-";
    if (isNeg) {
        temp = temp.slice(1);
    }

    let timeParts = temp.split(":");
    let hoursString = timeParts[0];
    let min = parseInt(timeParts[1], 10);
    let secondsString = timeParts[2] || "0.0";

    let secondParts = secondsString.split(".");
    let sec = parseInt(secondParts[0], 10);

    // Make sure millStr's length is at least 3.
    let mill;
    if (secondParts.length === 2) {
        let millStr = secondParts[1].concat("000");
        mill = parseInt(millStr.substring(0, 3), 10) || 0;
    } else {
        mill = 0;
    }

    let hourParts = hoursString.split(".");
    let hours: number;
    if (hourParts.length === 2) {
        days = parseInt(hourParts[0], 10);
        hours = parseInt(hourParts[1], 10);
    } else {
        hours = parseInt(hoursString, 10);
    }

    return (isNeg ? -1 : 1) * ((((days * 24 + hours) * 60 + min) * 60 + sec) * 1000 + mill);
}

export interface IRawViolation extends IViolation {
    drivingPeriods?: IPeriod[];
    lastDriveToDate?: Date;
}

export function getCleanViolation(rawViolation: IRawViolation): IViolation {
    return {
        type: rawViolation.type,
        fromDate: rawViolation.fromDate,
        toDate: rawViolation.toDate,
        logId: rawViolation.logId,
        reason: rawViolation.reason,
        drivingDuration: rawViolation.drivingDuration,
        hoursLimit: rawViolation.hoursLimit,
        daysLimit: rawViolation.daysLimit
    };
}

export let getRuleSetById = (id: string, useLegacyTexasShortHaulRulesets = false): IHosRuleset | undefined => {
    let ruleSetKey = id.replace("HosRuleSet", "");
    return getEffectiveRuleSets(useLegacyTexasShortHaulRulesets)[ruleSetKey];
};

let getRuleSetIndexById = (id: string): string => {
    return id.replace("HosRuleSet", "");
};

export let getRuleSetIdByIndex = (index: string): string => {
    return "HosRuleSet" + index;
};

export function getLatestRulesetNeedsReset(userRulesets: IUserHosRuleSet[], fromDate?: string | Date): IUserHosRuleSet | undefined {
    let rulesetsNeedReset = userRulesets
        .filter(userRuleSet => isRulesetCycleResetRequired(userRuleSet.hosRuleSet.id))
        .sort((a, b) => (a.dateTime > b.dateTime ? -1 : 1));
    if (fromDate) {
        let cleanFromDate = typeof fromDate === "string" ? fromDate : fromDate.toISOString();
        for (let i = 0; i < rulesetsNeedReset.length; i++) {
            if (rulesetsNeedReset[i].dateTime < cleanFromDate) {
                return rulesetsNeedReset[i];
            }
        }
        return undefined;
    }
    return rulesetsNeedReset[0];
}

export function isRulesetCycleResetRequired(ruleSetId: string): boolean {
    let ruleSetConfig = getRuleSetById(ruleSetId);
    return !!(ruleSetConfig && ruleSetConfig.config.resetPreviousCycle);
}

export function availToHours(value: string | undefined): number | undefined {
    if (value === undefined) {
        return undefined;
    }
    let hour = 1000 * 60 * 60;
    return timeSpanToMs(value) / hour;
}

export function getMinAvailability(availability: IHosAvailability): IMinAvailability {
    let min = Infinity;
    const props = ["driving", "cycle", "cycleDriving", "rest", "duty", "workday"] as const satisfies readonly (keyof IHosAvailability)[];
    type TMinAvailabilityKey = (typeof props)[number];
    let isMinAvailabilityKey = (key: string): key is TMinAvailabilityKey => props.some(prop => prop === key);
    let name = "";
    for (let prop in availability) {
        if (!isMinAvailabilityKey(prop)) {
            continue;
        }
        let value = availToHours(availability[prop]);
        if (value !== undefined && value < min) {
            min = value;
            name = prop;
        }
    }
    return { min: min, name: name };
}

export function getMinEwdTimeUntilMustRest(availability: IEwdWorkRestAvailability[]): IMinAvailability {
    let min = Infinity;
    availability.forEach(availPeriod => {
        if (availPeriod.timeUntilMustRest === undefined) {
            return;
        }

        let value = timeSpanToMs(availPeriod.timeUntilMustRest) / (1000 * 60 * 60);
        if (value !== undefined && value < min) {
            min = value;
        }
    });
    return { min };
}

export function getIconForAvailability(name: string): string {
    let icon;

    switch (name) {
        case "driving":
            icon = "drive-steering-wheel";
            break;
        case "cycle":
            icon = "drive-ccw";
            break;
        case "cycleDriving":
            icon = "drive-truck";
            break;
        case "rest":
            icon = "drive-coffee";
            break;
        case "workday":
            icon = "drive-tools";
            break;
        case "duty":
            icon = "drive-briefcase";
            break;
        default:
            icon = "";
    }

    return icon;
}

export function getAvailabilityName(name: string | undefined): string {
    let nameTranslated: string;
    switch (name) {
        case "driving":
            nameTranslated = "Driving left";
            break;
        case "cycle":
            nameTranslated = "Cycle left";
            break;
        case "cycleDriving":
            nameTranslated = "Cycle driving left";
            break;
        case "rest":
            nameTranslated = "Break required in";
            break;
        case "workday":
            nameTranslated = "Workday left";
            break;
        case "duty":
            nameTranslated = "Duty left";
            break;
        default:
            nameTranslated = "";
    }
    return nameTranslated;
}

export function getLatestWorkday(workdays: IWorkdaySummary[] | undefined): IWorkdaySummary | undefined {
    return workdays && workdays.length > 0 ? workdays.sort((a, b) => ((a.fromDate || 0) > (b.fromDate || 0) ? -1 : 1))[0] : undefined;
}

export function getCurrentCycle(cycles: ICycleSummary[]): ICycleSummary | undefined {
    return cycles.length > 0 ? cycles.sort((a, b) => (a.dateTime > b.dateTime ? -1 : 1))[0] : undefined;
}

export function getCurrentDay(days: IDaySummary[] | undefined): IDaySummary | undefined {
    return days && days.length > 0 ? days.sort((a, b) => (a.dateTime > b.dateTime ? -1 : 1))[0] : undefined;
}

export function isInCanada(rulesetId?: string) {
    return rulesetId ? !!rulesetId.match(/Canada|Alberta/gi) : false;
}

export function getSplitPeriodDuration(splitPeriod: ISplitPeriod): number {
    return splitPeriod.toDate.getTime() - splitPeriod.fromDate.getTime();
}

export function getOperatingZone(rulesetName: string): THosStatus {
    let cleanRulesetName = rulesetName.toLowerCase();
    let isCanada = cleanRulesetName.indexOf("canada") > -1;
    if (isCanada && cleanRulesetName.indexOf("north of 60") > -1) {
        return "OperatingZoneCanadaNorthOf60";
    }
    if (isCanada || cleanRulesetName.indexOf("alberta") > -1 || cleanRulesetName.indexOf("bc logging truck") > -1) {
        return "OperatingZoneCanadaSouthOf60";
    }
    return "OperatingZoneAmerica";
}

export function getOperatingZoneChange(oldRulesetId: string, newRulesetId: string): THosStatus[] | null {
    let oldRuleset = getRuleSetById(oldRulesetId);
    let newRuleset = getRuleSetById(newRulesetId);

    if (oldRuleset && newRuleset) {
        let oldOperatingZone = getOperatingZone(oldRuleset.name);
        let newOperatingZone = getOperatingZone(newRuleset.name);

        if (oldOperatingZone !== newOperatingZone) {
            return [oldOperatingZone, newOperatingZone];
        }
    }
    return null;
}

export function getCodeForLog(malfunction: THosMalfunction | undefined, status: THosStatus | TDutyStatusLogStatus): string {
    if (malfunction && malfunction.indexOf("Malfunction") > -1) {
        switch (status) {
            case "PowerCompliance":
                return "P";
            case "EngineSyncCompliance":
                return "E";
            case "TimingCompliance":
                return "T";
            case "PositioningCompliance":
                return "L";
            case "DataRecordingCompliance":
                return "R";
            case "DataTransferCompliance":
                return "S";
            default:
                return "O";
        }
    } else {
        switch (status) {
            case "PowerCompliance":
                return "1";
            case "EngineSyncCompliance":
                return "2";
            case "MissingElementCompliance":
                return "3";
            case "DataTransferCompliance":
                return "4";
            case "UnidentifiedDrivingCompliance":
                return "5";
            default:
                return "6";
        }
    }
}

function isCycleOneLog(rulesetId: string): boolean {
    return CYCLEONERULESETS.indexOf(rulesetId) > -1;
}

function isCycleTwoLog(rulesetId: string): boolean {
    return CYCLETWORULESETS.indexOf(rulesetId) > -1;
}

export function getCycle(rulesetId: string): THosStatus | undefined {
    const sanitizedRulesetId = rulesetId.replace("HosRuleSet", "");
    return isCycleOneLog(sanitizedRulesetId) ? "CanadaCycleOne" : isCycleTwoLog(sanitizedRulesetId) ? "CanadaCycleTwo" : undefined;
}

export function getCycleChange(oldRulesetId: string, newRulesetId: string): THosStatus[] | null {
    if (oldRulesetId && newRulesetId) {
        let oldCycle = getCycle(oldRulesetId);
        let newCycle = getCycle(newRulesetId);

        if (oldCycle && newCycle && oldCycle !== newCycle) {
            return [oldCycle, newCycle];
        }
    }
    return null;
}

/**
 * Determines the jurisdiction of the provided ruleset ID (ELD, CANELD, EWD)
 * @param rulesetId The ID of the ruleset who's jurisdiction we want to get
 */
export function getELDJurisdiction(rulesetId?: string) {
    if (rulesetId) {
        if (isInCanada(rulesetId)) {
            return "CANELD";
        }

        if (AUSTRALIARULESETS.indexOf(rulesetId) !== -1) {
            return "EWD";
        }
    }

    return "ELD";
}

export function roundBasedOnStatus(dateTime: Date, status?: THosStatus) {
    let currentMinute = dateTime.getMinutes();
    if (status && status === "Work") {
        dateTime.setMinutes(Math.ceil(currentMinute / 15) * 15);
    } else {
        dateTime.setMinutes(Math.floor(currentMinute / 15) * 15);
    }
    return dateTime;
}

export function roundBasedOnCurrentStatus(dateTime: Date, status: THosStatus, currentStatus?: THosStatus) {
    let minutes = 15;
    let ms = 1000 * 60 * minutes;
    let roundedDate: Date;

    // If no current status OR current status is not EWD, round DOWN to the nearest 15 minute interval
    if (!currentStatus || ["Work", "Rest", "WorkExemption"].indexOf(currentStatus) === -1) {
        roundedDate = new Date(Math.floor(dateTime.getTime() / ms) * ms);
    } else if ((currentStatus === "Work" || currentStatus === "WorkExemption") && status === "Rest") {
        // If going WORK/WORKEXEMPTION --> REST, new log's time is rounded UP to the nearest 15 minute interval
        roundedDate = new Date(Math.ceil((dateTime.getTime() + 0.5) / ms) * ms);
    } else {
        // If going REST --> WORK or entering WORKEXEMPTION, new log's time is rounded DOWN to the nearest 15 minute interval
        roundedDate = new Date(Math.floor(dateTime.getTime() / ms) * ms);
    }

    return roundedDate;
}

export function getHoursInCycle(date: Date, daySummaries?: IDaySummary[]): number {
    let hoursInCycle = 0;
    daySummaries?.forEach(summary => {
        if (summary.dateTime.getTime() === date.getTime()) {
            hoursInCycle = timeSpanToMs(summary.hoursInCycle);
        }
    });

    return hoursInCycle;
}

export function getCycleInfo(date: Date, daySummaries: IDaySummary[] | undefined, rulesetMaximum?: string) {
    if (rulesetMaximum) {
        // Get totalHoursInCycle from daySummaries
        let totalHoursInCycle = getHoursInCycle(date, daySummaries);

        // Determine hours remaining in cycle
        let totalHoursRemainingInCycle = timeSpanToMs(rulesetMaximum) - totalHoursInCycle;

        // If cycle maximum exceeded (i.e. remaining hours is negative), just show zero instead of negative number
        totalHoursRemainingInCycle = Math.max(0, totalHoursRemainingInCycle);

        // Limit for the print value is 336 hours / 14 days
        totalHoursRemainingInCycle = Math.min(14 * DAYS, totalHoursRemainingInCycle);
        totalHoursInCycle = Math.min(14 * DAYS, totalHoursInCycle);

        return [msToShortTimespan(totalHoursInCycle, 3), msToShortTimespan(totalHoursRemainingInCycle, 3)];
    }

    return ["N/A", "N/A"];
}

export function getHoursInWorkShift(recap: IHosRecap[] | undefined, date?: Date) {
    let timeInWorkShift = 0;

    // Work shift is pulled from the most recent day
    if (recap?.length) {
        if (date) {
            let recapsForDate = recap.filter(rc => rc.dateTime.toDateString() === date.toDateString());

            if (recapsForDate.length) {
                timeInWorkShift = timeSpanToMs(recapsForDate[recapsForDate.length - 1].duration) || 0;
            }
        } else {
            timeInWorkShift = timeSpanToMs(recap[recap.length - 1].duration) || 0;
        }
    }

    return msToShortTimespan(timeInWorkShift);
}

export function getHoursInWorkShiftFromWorkdaySummary(
    workdaySummaries: IWorkdaySummary[],
    targetDate: Date,
    targetDateEnd: Date
): string {
    if (workdaySummaries.length === 0) {
        return msToShortTimespan(0);
    }

    const targetInterval = { start: targetDate.getTime(), end: targetDateEnd.getTime() };

    const inProgressWorkday = workdaySummaries.find(workday => workday.isInProgress);
    if (inProgressWorkday?.fromDate) {
        if (inProgressWorkday.fromDate.getTime() < targetDateEnd.getTime()) {
            let timeInWorkShift = inProgressWorkday.onDutyCount ? timeSpanToMs(inProgressWorkday.onDutyCount) : 0;
            return msToShortTimespan(timeInWorkShift);
        }
    }

    let maxIntervalMs = 0;
    let maxWorkday: IWorkdaySummary | undefined;

    for (const workday of workdaySummaries) {
        const intervals = (workday.onDutyIntervals || []).map(i => ({
            start: i.start.getTime(),
            end: i.end.getTime()
        }));
        const trimmed = intervals.map(interval => ({
            start: Math.max(interval.start, targetInterval.start),
            end: Math.min(interval.end, targetInterval.end)
        })).filter(interval => interval.end > interval.start);
        const totalMs = DutyStatusIntervalTracker.getTotalDuration(trimmed);
        if (totalMs > 0 && totalMs >= maxIntervalMs) {
            maxIntervalMs = totalMs;
            maxWorkday = workday;
        }
    }

    let timeInWorkShift = maxWorkday?.onDutyCount ? timeSpanToMs(maxWorkday.onDutyCount) : 0;
    return msToShortTimespan(timeInWorkShift);
}

export function hasUserDrivenInLatestWorkday(ruleResult: IValidation): boolean {
    let workdays = ruleResult.workdaySummaries || [];
    let latestWorkday = workdays.length ? workdays[workdays.length - 1] : undefined;
    return latestWorkday === undefined ? false : latestWorkday.driveCount !== undefined && timeSpanToMs(latestWorkday.driveCount) > 0;
}

export function msToHours(ms: number): number {
    return Math.round(ms / 1000 / 60 / 60);
}

export function parseDateTime(value: any): Date {
    if (typeof value === "string" && reISO.exec(value)) {
        return new Date(value);
    }
    return value;
}

export function isPositionComplianceLog(log: IDutyStatusLog) {
    return log.status === "PositioningCompliance";
}

export function isPositionDiagnosticLog(log: IDutyStatusLog) {
    return isPositionComplianceLog(log) && (log.malfunction === "Diagnostic" || log.malfunction === "SystemDiagnosticClear");
}

export function isMotionStoppedLog(log: IDutyStatusLog) {
    return log.status === "MotionStopped";
}

export function trimMalfunctionDiagnosticLogs(malfunctionLogCollection: IDutyStatusLog[]) {
    if (malfunctionLogCollection.length <= 10) {
        return malfunctionLogCollection;
    }

    let diagnosticCount = 0;
    let malfunctionCount = 0;
    let malfunctionsDiagnosticsToSave: IDutyStatusLog[] = [];
    let allMalfunctions = malfunctionLogCollection.sort((a, b) => compareDates(b.dateTime, a.dateTime));

    for (let i = allMalfunctions.length - 1; i >= 0; i--) {
        if (diagnosticCount === 10 && malfunctionCount === 10) {
            break;
        }

        let log = allMalfunctions[i];
        if (log.malfunction) {
            if (log.malfunction.indexOf("Diagnostic") > -1) {
                if (diagnosticCount < 10) {
                    diagnosticCount++;
                    malfunctionsDiagnosticsToSave.push(log);
                }
            } else if (log.malfunction.indexOf("Malfunction") > -1) {
                if (malfunctionCount < 10) {
                    malfunctionCount++;
                    malfunctionsDiagnosticsToSave.push(log);
                }
            }
        }
    }

    return malfunctionsDiagnosticsToSave;
}

interface ILocalizer {
    toShortDate: (dateTime: string, timeZoneId?: string, format?: string) => string;
}

/**
 * Logic for filtering of Diagnostic/Malfunction logs for Canada ELD Report.
 * @param diagnosticMalfunctionLogs List of Diagnostic/Malfunction logs
 * @param workedDays List of days worked, keyed by vehicle ID.
 * @param localizer
 * @param timeZoneId
 * @returns An array containing all malfunction logs on worked days and a dictionary which contains the latest malfunction/diagnostic per day.
 */
export function filterDiagnosticMalfunctionLogsForCanadaEldReport(
    diagnosticMalfunctionLogs: IDutyStatusLog[],
    workedDays: { [key: string]: string[] },
    localizer: ILocalizer,
    timeZoneId?: string
): [IDutyStatusLog[], ILatestMalfunctionDiagnosticDictionary] {
    let allMalfunctions = diagnosticMalfunctionLogs.sort((a, b) => compareDates(b.dateTime, a.dateTime));

    type TMalfunctionDiagnosticStatus = keyof ILatestMalfunctionDiagnosticDictionary;
    const malfunctionDiagnosticStatuses: TMalfunctionDiagnosticStatus[] = ["malfunction", "diagnostic"];
    let activeMalfunctionsDictionary: Required<ILatestMalfunctionDiagnosticDictionary<IDutyStatusLog[]>> = {
        malfunction: {},
        diagnostic: {}
    };
    let latestMalfunctionDiagnosticDictionary: Required<ILatestMalfunctionDiagnosticDictionary> = {
        malfunction: {},
        diagnostic: {}
    };

    let filteredMalfunctions: IDutyStatusLog[] = [];

    let index = 0;

    // Iterate through each worked day in order to get
    // 1) The latest active malfunction + diagnostic
    // 2) The malfunction/diagnostic logs created each worked day
    Object.keys(workedDays)
        .sort()
        .forEach(dateString => {
            for (index; index < allMalfunctions.length; index++) {
                let malfunction = allMalfunctions[index];
                let malfunctionDate = localizer.toShortDate(malfunction.dateTime, timeZoneId, "yyyy-MM-dd");

                if (malfunctionDate > dateString) {
                    break;
                }

                if (isPositionDiagnosticLog(malfunction)) {
                    continue;
                }

                if (malfunction.malfunction) {
                    let malfunctionStatus: TMalfunctionDiagnosticStatus =
                        malfunction.malfunction.indexOf("Malfunction") > -1 ? "malfunction" : "diagnostic";
                    let activeStatusDictionary = activeMalfunctionsDictionary[malfunctionStatus];

                    let deviceId = getDeviceId(malfunction.device) || "NoDeviceId";
                    if (workedDays.hasOwnProperty(malfunctionDate) && workedDays[malfunctionDate].indexOf(deviceId) > -1) {
                        filteredMalfunctions.push(malfunction);

                        let activeMalfunctionsForDevice = activeStatusDictionary[deviceId];
                        if (activeMalfunctionsForDevice?.length) {
                            latestMalfunctionDiagnosticDictionary[malfunctionStatus][malfunctionDate] =
                                activeMalfunctionsForDevice[activeMalfunctionsForDevice.length - 1];
                        }
                    }

                    if (!activeStatusDictionary.hasOwnProperty(deviceId)) {
                        activeStatusDictionary[deviceId] = [];
                    }

                    if (malfunction.malfunction.indexOf("Clear") > -1) {
                        activeStatusDictionary[deviceId] = activeStatusDictionary[deviceId].filter(log => log.status !== malfunction.status);
                    } else {
                        activeStatusDictionary[deviceId].push(malfunction);
                    }
                }
            }

            // If there are days with no malfunctions, check if there were any malfunctions/diagnostics that were not cleared
            malfunctionDiagnosticStatuses.forEach(malfunctionStatus => {
                let activeStatusDictionary = activeMalfunctionsDictionary[malfunctionStatus];

                workedDays[dateString].forEach(deviceId => {
                    let activeMalfunctionsForDevice = activeStatusDictionary[deviceId];
                    if (activeMalfunctionsForDevice?.length) {
                        let currentLog = latestMalfunctionDiagnosticDictionary[malfunctionStatus][dateString];
                        let newLog = activeMalfunctionsForDevice[activeMalfunctionsForDevice.length - 1];
                        if (!currentLog || currentLog.dateTime < newLog.dateTime) {
                            latestMalfunctionDiagnosticDictionary[malfunctionStatus][dateString] = newLog;
                        }
                    }
                });
            });
        });

    return [filteredMalfunctions, latestMalfunctionDiagnosticDictionary];
}

export function getDateNotTime(date: Date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function getUtcDateNotTime(date: Date) {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function formatUtcDateString(date: Date) {
    return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`;
}

export function isVerifyDateAtMidnight(verifyDateTime: string) {
    return verifyDateTime.endsWith("00:00:00.000Z");
}

export function formatVerifyDateTimeToString(verifyDateTime: string, toLocalDateTime: (date: Date | string, timeZoneId?: string) => Date) {
    // If the verifydatetime is exactly at midnight, just use the date
    if (isVerifyDateAtMidnight(verifyDateTime)) {
        return verifyDateTime.substring(0, 10);
    }

    // Otherwise, adjust based on timezone
    let date = toLocalDateTime(new Date(verifyDateTime));
    return formatUtcDateString(date);
}

interface ICertifiedLog {
    status: string;
    verifyDateTime?: string | undefined;
    dateTime: string;
}

export function getFirstCertifyLogDict<T extends ICertifiedLog>(logs: T[], toLocalDateTime: (date: Date | string, timeZoneId?: string) => Date) {
    let firstCertifyLogDict = {} as IIdDictionary<T>;

    logs.forEach(log => {
        if (isCertifyLog(log) && log.verifyDateTime) {
            let dateString = formatVerifyDateTimeToString(log.verifyDateTime, toLocalDateTime);
            if (!firstCertifyLogDict.hasOwnProperty(dateString) || firstCertifyLogDict[dateString].dateTime > log.dateTime) {
                firstCertifyLogDict[dateString] = log;
            }
        }
    });

    return firstCertifyLogDict;
}

/**
 * Get a list of device association intervals for a driver using a list of driver changes.
 * @param allDriverChanges All driver changes for the user and the vehicles they used throughout the range.
 * @param driver The user to get the device association intervals for.
 * @param toDate The end of the range.
 * @returns A list of IDeviceAssociation objects.
 */
export function getDeviceAssociationIntervalsImpl(allDriverChanges: IDriverChange[], driver: IIdEntity, toDate: Date) {
    let updateOrAddAssociationInterval = (
        deviceAssociationIntervals: IDeviceAssociation[],
        deviceId: string,
        intervalFromDate: Date,
        intervalToDate: Date
    ) => {
        let lastDeviceInterval = deviceAssociationIntervals[deviceAssociationIntervals.length - 1];
        if (
            lastDeviceInterval &&
            lastDeviceInterval.deviceId === deviceId &&
            intervalFromDate.getTime() - lastDeviceInterval.toDate.getTime() <= MINUTES * 30
        ) {
            // If the device range is within a certain grace period from the previous interval, just extend the previous interval (same in EldDataCalculator)
            lastDeviceInterval.toDate = intervalToDate;
        } else {
            deviceAssociationIntervals.push({
                deviceId,
                fromDate: intervalFromDate,
                toDate: intervalToDate
            });
        }
    };

    let currentDeviceId: string | undefined = undefined;
    let currentFromDate: Date | undefined = undefined;
    let deviceAssociationIntervals: IDeviceAssociation[] = [];
    let driverId = driver.id;
    if (driverId === "NoUserId") {
        driverId = "UnknownDriverId";
    }
    let areDriversEqual = (driverChangeDriver: IIdEntity | "UnknownDriverId") =>
        driverId === driverChangeDriver || driverId === (driverChangeDriver as IIdEntity).id;

    allDriverChanges.forEach(driverChange => {
        if ((!currentDeviceId || !currentFromDate || driverChange.device.id !== currentDeviceId) && areDriversEqual(driverChange.driver)) {
            if (currentDeviceId && currentFromDate) {
                // Driver changed to a different vehicle, end the current interval
                updateOrAddAssociationInterval(deviceAssociationIntervals, currentDeviceId, currentFromDate, new Date(driverChange.dateTime));
            }
            currentDeviceId = driverChange.device.id;
            currentFromDate = new Date(driverChange.dateTime);
        } else if (currentDeviceId && currentFromDate && driverChange.device.id === currentDeviceId && !areDriversEqual(driverChange.driver)) {
            // Another driver else claimed this vehicle or it was set to unassigned, end the current interval
            updateOrAddAssociationInterval(deviceAssociationIntervals, currentDeviceId, currentFromDate, new Date(driverChange.dateTime));
            currentDeviceId = undefined;
            currentFromDate = undefined;
        }
    });
    if (currentDeviceId && currentFromDate) {
        // If the driver is still driving at the end of the report period, end the current interval with the report's "toDate"
        updateOrAddAssociationInterval(deviceAssociationIntervals, currentDeviceId, currentFromDate, toDate);
    }

    return deviceAssociationIntervals;
}

export function compareDatesWithSequenceIds<TDate extends string | Date>(a: ISlimDutyStatusLog<TDate>, b: ISlimDutyStatusLog<TDate>) {
    if (typeof a.dateTime !== typeof b.dateTime) {
        throw new Error("Invalid date sort parameter types");
    }

    if (a.dateTime > b.dateTime) {
        return 1;
    }

    if (a.dateTime < b.dateTime) {
        return -1;
    }

    if (a.sequence && b.sequence) {
        return a.sequence > b.sequence ? 1 : a.sequence < b.sequence ? -1 : 0;
    }

    if (a.sequence) {
        return -1;
    }

    if (b.sequence) {
        return 1;
    }

    return 0;
}

// Hack for drive TypeScript types
export interface IHelpers {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    HOURS: typeof HOURS;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MINUTES: typeof MINUTES;
    hasExemption: typeof hasExemption;
    hasCustomStartOfDay: typeof hasCustomStartOfDay;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    RULESETS: typeof RULESETS;
    defPhrases: typeof defPhrases;
    getLogRetentionDays: typeof getLogRetentionDays;
    isStatusLog: typeof isStatusLog;
    isAustralianStatusLog: typeof isAustralianStatusLog;
    isSupportedCanadaEldLog: typeof isSupportedCanadaEldLog;
    getFilteredRuleSets: typeof getFilteredRuleSets;
    getRuleSetName: typeof getRuleSetName;
    getDefaultAvailability: typeof getDefaultAvailability;
    getRuleSetById: typeof getRuleSetById;
    getRuleSetIdByIndex: typeof getRuleSetIdByIndex;
    isRulesetCycleResetRequired: typeof isRulesetCycleResetRequired;
    getLatestRulesetNeedsReset: typeof getLatestRulesetNeedsReset;
    isRuleSetChanged: typeof isRuleSetChanged;
    getRawRuleSet: typeof getRawRuleSet;
    timeSpanToMs: typeof timeSpanToMs;
    availToHours: typeof availToHours;
    getMinAvailability: typeof getMinAvailability;
    getMinEwdTimeUntilMustRest: typeof getMinEwdTimeUntilMustRest;
    getIconForAvailability: typeof getIconForAvailability;
    getLatestWorkday: typeof getLatestWorkday;
    getCurrentCycle: typeof getCurrentCycle;
    getCurrentDay: typeof getCurrentDay;
    populateViolationReason: typeof populateViolationReason;
    onDutyStatuses: typeof onDutyStatuses;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    AUSTRALIARULESETS: typeof AUSTRALIARULESETS;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CANADARULESETS: typeof CANADARULESETS;
    getRuleSets: typeof getRuleSets;
    getAllRuleSets: typeof getAllRuleSets;
    recreateRuleSetsList: typeof recreateRuleSetsList;
    isInCanada: typeof isInCanada;
    getOperatingZone: typeof getOperatingZone;
    getOperatingZoneChange: typeof getOperatingZoneChange;
    getCycle: typeof getCycle;
    getOperatingZoneFromRuleSet: typeof getOperatingZoneFromRuleSet;
    getCycleChange: typeof getCycleChange;
    msToTimespan: typeof msToTimespan;
    getCodeForLog: typeof getCodeForLog;
    getELDJurisdiction: typeof getELDJurisdiction;
    roundBasedOnStatus: typeof roundBasedOnStatus;
    roundBasedOnCurrentStatus: typeof roundBasedOnCurrentStatus;
    getHoursInCycle: typeof getHoursInCycle;
    msToHours: typeof msToHours;
    getCycleInfo: typeof getCycleInfo;
    getHoursInWorkShift: typeof getHoursInWorkShift;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    DeferralStatus: typeof DeferralStatus;
    parseDateTime: typeof parseDateTime;
    getDateNotTime: typeof getDateNotTime;
    getUtcDateNotTime: typeof getUtcDateNotTime;
    formatUtcDateString: typeof formatUtcDateString;
    formatVerifyDateTimeToString: typeof formatVerifyDateTimeToString;
    getFirstCertifyLogDict: typeof getFirstCertifyLogDict;
    isVerifyDateAtMidnight: typeof isVerifyDateAtMidnight;
    compareDatesWithSequenceIds: typeof compareDatesWithSequenceIds;
}

export enum TranslatableDisplayStrings {
    STATUS = "Status",
    REST_DURATION = "Rest duration",
    DRIVING_BREAK_IN = "Break required in",
    ALTERNATE_DRIVING_BREAK_IN = "{0} Break required in",
    DRIVING_BREAK = "Break",
    ALTERNATE_DRIVING_BREAK = "{0} Break",
    DRIVING_BREAK_DURATION = "Break duration",
    ALTERNATE_DRIVING_BREAK_DURATION = "{0} Break duration",
    DRIVING_LEFT = "Driving left",
    DUTY_LEFT = "Duty left",
    WORKDAY_LEFT = "Workday left",
    CYCLE_LEFT = "Cycle left",
    CYCLE_DRIVING_LEFT = "Cycle driving left",
    TIME_LEFT_UNTIL_24_HOUR_REST = "Time left until 24-hour rest",
    DUTY_LEFT_UNTIL_24_HOUR_REST = "Duty left until 24-hour rest",
    REST_REQUIRED = "Rest required",
    OFF_DUTY_REQUIRED = "Off-duty required",
    DAYS_STRING = "{0} days",
    MINUTES_STRING = "{minutes}m",
    HOUR_UNIT = "h",
    MINUTE_UNIT = "m",
    DAYS_UNIT = "d",
    SECONDS_UNIT = "s",
    USER = "User",
    DRIVE_APP_HOURS_OF_SERVICE = "Drive App Hours of Service",
    TAP_TO_LOGIN = "Tap to log in",
    TAP_TO_COMPLETE_WORKFLOW = "Tap to complete workflow",
    COMPLIANCE = "Compliance",
    Unknown = "Unknown"
}

export function convertDecimalToTimeString(decimal: string | number, useFloor?: boolean): string {
    let decimalFloat = parseFloat(decimal.toString());
    let hours = Math.floor(decimalFloat);
    let minutes = !useFloor ? Math.ceil((decimalFloat - hours) * 60) : Math.floor((decimalFloat - hours) * 60);

    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }

    let hoursString = "";
    if (hours > 0) {
        hoursString = `${hours}{0} `;
    }

    return `${hoursString}${minutes}{1}`;
}

export function convertToTimeString(availabilityTime: string | number, translatedHourUnit: string, translatedMinuteUnit: string, useFloor = false) {
    let timeString = convertDecimalToTimeString(availabilityTime, useFloor);
    return timeString.replace("{0}", translatedHourUnit).replace("{1}", translatedMinuteUnit);
}

export function convertMsToString(
    availabilityTime: string | number,
    hourUnit: string = TranslatableDisplayStrings.HOUR_UNIT,
    minuteUnit: string = TranslatableDisplayStrings.MINUTE_UNIT,
    useFloor = false
) {
    return convertToTimeString(availabilityTime, hourUnit, minuteUnit, useFloor);
}

export function isDrivingBreakStatus(status: TDutyStatusLogStatus) {
    return ["ON", "YM", "OFF", "PC", "WT", "SB", "PC_Exempted"].indexOf(status) > -1;
}

export function isOFFDutyStatus(status: TDutyStatusLogStatus) {
    return ["OFF", "SB", "PC", "PC_Exempted", "WT", "Rest"].indexOf(status) > -1;
}

export const getReportStatuses = (isAustraliaUser: boolean, includeIntermediate: boolean, includeExemptions: boolean) => {
    let reportStatuses = STATUS_LOG_TYPES.slice();

    if (isAustraliaUser) {
        reportStatuses = reportStatuses.concat(ALL_AUSTRALIAN_LOG_TYPES);
    }

    if (includeIntermediate) {
        reportStatuses = reportStatuses.concat(INTERMEDIATE_LOG_TYPES);
    }

    if (includeExemptions) {
        reportStatuses = reportStatuses.concat(AUTHORITY_LOG_TYPES).concat(CONTROL_LOG_TYPES).concat(OTHER_LOG_TYPES);
    }

    return reportStatuses;
};

export type TAvailabilityFn = (state: IState, enforceNonNegative?: boolean) => number | undefined;
export const nonNegativeAvailabilityFn = (fn: TAvailabilityFn) => {
    return (state: IState, enforceNonNegative = false) => {
        const result = fn(state);
        return enforceNonNegative && result !== undefined && result < 0 ? 0 : result;
    };
};

export type TNextAvailabilityEventFn = (state: IState) => INextAvailabilityEvent | undefined;
